import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatEUR, formatDate, ROLE_LABELS } from "@/lib/constants";
import {
  ListChecks, House, CurrencyEur, ChartBar, Warning, Buildings,
  IdentificationCard, ArrowRight, Wrench, Bank, TrendUp, Calendar,
  Briefcase, Clock, ChatCircleText, EnvelopeSimple, X, NotePencil, PaperPlaneTilt,
  DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import MessagingDialog from "@/components/MessagingDialog";
import RentIncreaseAlerts from "@/components/rentincrease/RentIncreaseAlerts";
import RunningRentIncreases from "@/components/rentincrease/RunningRentIncreases";
import UpcomingHandovers from "@/components/tenancy/UpcomingHandovers";

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [myTasks, setMyTasks] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [vorgangSummary, setVorgangSummary] = useState(null);
  const [emailAlerts, setEmailAlerts] = useState([]);
  // 2026-08 — „Meine offenen Entwürfe" (letzte 7 Tage)
  const [myDrafts, setMyDrafts] = useState([]);
  const [activeDraft, setActiveDraft] = useState(null);
  // 2026-02 — Offene Kautionsabrechnungen
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const { user } = useAuth();
  const isAdmin = user && ["super_admin", "admin", "platform_admin"].includes(user.role);

  const loadEmailAlerts = () => {
    api.get("/notifications/email").then((r) => setEmailAlerts(r.data || [])).catch(() => setEmailAlerts([]));
  };

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data)).catch(() => {});
    api.get("/vorgaenge/dashboard/summary").then((r) => setVorgangSummary(r.data)).catch(() => setVorgangSummary({}));
    loadEmailAlerts();
    if (user?.id && !isAdmin) {
      api.get("/tasks", { params: { mine: true, status: "open" } })
        .then((r) => setMyTasks((r.data || []).slice(0, 10))).catch(() => {});
      api.get("/tickets", { params: { assigned_user_id: user.id } })
        .then((r) => setMyTickets((r.data || []).filter((t) => t.status !== "done" && t.status !== "closed").slice(0, 10))).catch(() => {});
    }
    // Drafts der letzten 7 Tage (eigene) — verfügbar für alle Rollen, die Schreiben dürfen
    api.get("/messages/drafts", { params: { limit: 30 } })
      .then((r) => {
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const list = (r.data || []).filter((d) => new Date(d.updated_at).getTime() >= cutoff);
        setMyDrafts(list.slice(0, 10));
      })
      .catch(() => setMyDrafts([]));
    // 2026-02 — Offene Kautionsabrechnungen für beendete MVs
    api.get("/kaution/pending-settlements")
      .then((r) => setPendingDeposits((r.data?.items || []).slice(0, 20)))
      .catch(() => setPendingDeposits([]));
  }, [user?.id, isAdmin]);

  const dismissAlert = async (id) => {
    try {
      await api.post(`/notifications/email/${id}/dismiss`);
      setEmailAlerts((prev) => prev.filter((e) => e.id !== id));
    } catch { toast.error("Konnte nicht ausgeblendet werden"); }
  };
  const dismissAllAlerts = async () => {
    try {
      await api.post(`/notifications/email/dismiss-all`);
      setEmailAlerts([]);
      toast.success("Alle Hinweise ausgeblendet");
    } catch { toast.error("Fehler"); }
  };

  if (!kpis) {
    return <div className="text-slate-500 text-sm">Lade Kennzahlen...</div>;
  }

  const today = new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white rounded-md px-6 py-7 relative overflow-hidden">
        <div className="absolute -right-12 -top-12 w-64 h-64 bg-white/5 rounded-full"></div>
        <div className="absolute -right-20 -bottom-20 w-72 h-72 bg-blue-400/5 rounded-full"></div>
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2">
            <Calendar size={12} /> {today}
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Willkommen zurück, {user?.name?.split(" ")[0] || "Verwalter"}</h1>
          <p className="text-sm text-slate-300 mt-1.5">{ROLE_LABELS[user?.role] || user?.role} · Domivus Verwaltungsplattform</p>

          {/* Inline quick stats */}
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-5 text-sm">
            <InlineStat icon={<Buildings size={14} />} label="Objekte" value={kpis.properties_total} />
            <InlineStat icon={<House size={14} />} label="Einheiten" value={kpis.units_total} />
            <InlineStat icon={<IdentificationCard size={14} />} label="Kontakte" value={kpis.contacts_total} />
            <InlineStat icon={<TrendUp size={14} />} label="Belegung" value={`${kpis.occupancy_rate}%`} />
          </div>
          {isAdmin && <DbExportButton />}
        </div>
      </div>

      {/* Phase VM-6 — E-Mail Bounce/Complaint Notifications */}
      {emailAlerts.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-md overflow-hidden" data-testid="email-alerts-panel">
          <div className="flex items-center justify-between px-4 py-2 bg-rose-100/50 border-b border-rose-200">
            <div className="flex items-center gap-2 text-sm font-semibold text-rose-900">
              <EnvelopeSimple size={16} weight="fill" />
              {emailAlerts.length} E-Mail-Zustellprobleme
            </div>
            <button
              onClick={dismissAllAlerts}
              className="text-xs text-rose-700 hover:text-rose-900 font-medium"
              data-testid="dismiss-all-email-alerts">
              Alle ausblenden
            </button>
          </div>
          <div className="divide-y divide-rose-100 max-h-52 overflow-y-auto">
            {emailAlerts.slice(0, 5).map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`email-alert-${e.id}`}>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${e.type === "email.bounced" ? "bg-rose-200 text-rose-900" : "bg-amber-200 text-amber-900"}`}>
                  {e.type === "email.bounced" ? "Bounce" : "Beschwerde"}
                </span>
                <div className="flex-1 min-w-0 truncate text-slate-800">
                  <span className="font-medium">{e.subject || "(ohne Betreff)"}</span>
                  <span className="text-xs text-slate-500 ml-2">an {e.data?.to?.[0] || e.data?.email || "—"}</span>
                </div>
                <span className="text-xs text-slate-500 tabular-nums">{formatDate(e.received_at)}</span>
                <button
                  onClick={() => dismissAlert(e.id)}
                  className="p-1 rounded hover:bg-rose-200 text-rose-700"
                  title="Ausblenden"
                  data-testid={`dismiss-email-alert-${e.id}`}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi to="/aufgaben" icon={<ListChecks size={18} />} label="Offene Aufgaben"
          value={kpis.open_tasks} sub={kpis.overdue_tasks > 0 ? `${kpis.overdue_tasks} überfällig` : "Im Plan"}
          subColor={kpis.overdue_tasks > 0 ? "text-rose-700" : "text-emerald-700"} testid="kpi-tasks" />
        <Kpi to="/tickets" icon={<Wrench size={18} />} label="Offene Tickets"
          value={kpis.open_tickets ?? 0} sub="Anliegen & Schäden"
          subColor="text-slate-500" testid="kpi-tickets" accent={kpis.open_tickets > 0 ? "amber" : null} />
        <Kpi to="/bank" icon={<Bank size={18} />} label="Bank-Klärung"
          value={kpis.unmatched_bank ?? 0} sub="Unzugeordnete Buchungen"
          subColor={kpis.unmatched_bank > 0 ? "text-amber-700" : "text-emerald-700"} testid="kpi-bank" />
        <Kpi to="/mietverhaeltnisse" icon={<CurrencyEur size={18} />} label="Mietrückstände"
          value={formatEUR(kpis.arrears_amount)}
          sub={kpis.arrears_tenancies ? `${kpis.arrears_tenancies} Mietverhältnisse` : "Keine Rückstände"}
          subColor={kpis.arrears_amount > 0 ? "text-rose-700" : "text-emerald-700"} testid="kpi-arrears" />
        <Kpi to="/objekte" icon={<House size={18} />} label="Leerstand"
          value={kpis.vacant_units} sub={`${kpis.occupancy_rate}% belegt`}
          subColor="text-slate-500" testid="kpi-vacant" />
        <Kpi to="/mietverhaeltnisse" icon={<ChartBar size={18} />} label="Aktive Verträge"
          value={kpis.active_tenancies} sub={`${kpis.ending_soon} enden bald`}
          subColor={kpis.ending_soon > 0 ? "text-amber-700" : "text-slate-500"} testid="kpi-active" />
      </div>

      {/* Phase VM-5 — Vorgangs-Widgets */}
      <RunningRentIncreases />
      <UpcomingHandovers />
      <RentIncreaseAlerts />
      {vorgangSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vorgang-widgets">
          <Kpi to="/vorgaenge" icon={<Briefcase size={18} />} label="Offene Vorgänge"
            value={vorgangSummary.open ?? 0}
            sub={vorgangSummary.assigned_to_me > 0 ? `${vorgangSummary.assigned_to_me} mir zugewiesen` : "Alle Bearbeiter"}
            subColor="text-slate-500" testid="kpi-vorgang-open"
            accent={vorgangSummary.open > 0 ? "amber" : null} />
          <Kpi to="/vorgaenge" icon={<Warning size={18} />} label="Überfällige Vorgänge"
            value={vorgangSummary.overdue ?? 0}
            sub={vorgangSummary.overdue > 0 ? "Frist überschritten" : "Alles im Zeitplan"}
            subColor={vorgangSummary.overdue > 0 ? "text-rose-700" : "text-emerald-700"}
            testid="kpi-vorgang-overdue"
            accent={vorgangSummary.overdue > 0 ? "rose" : null} />
          <Kpi to="/vorgaenge" icon={<Clock size={18} />} label="Fristen heute"
            value={vorgangSummary.reminders_today ?? 0}
            sub={vorgangSummary.reminders_today > 0 ? "Wiedervorlagen fällig" : "Keine für heute"}
            subColor={vorgangSummary.reminders_today > 0 ? "text-amber-700" : "text-slate-500"}
            testid="kpi-vorgang-reminders"
            accent={vorgangSummary.reminders_today > 0 ? "amber" : null} />
          <Kpi to="/vorgaenge" icon={<ChatCircleText size={18} />} label="Wartet auf Antwort"
            value={vorgangSummary.waiting_reply ?? 0}
            sub="Externe Rückmeldung offen"
            subColor="text-slate-500" testid="kpi-vorgang-waiting" />
        </div>
      )}

      {/* 2026-02 — Offene Kautionsabrechnungen (beendete MVs ohne Abrechnung) */}
      {pendingDeposits.length > 0 && (
        <div className="bg-amber-50/50 border border-amber-200 rounded-md p-4" data-testid="dashboard-pending-deposits">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Warning size={16} className="text-amber-700" />
              <h2 className="text-sm font-semibold tracking-tight text-amber-900">
                Offene Kautionsabrechnungen
              </h2>
              <span className="text-xs text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                {pendingDeposits.length}
              </span>
            </div>
            <span className="text-[11px] text-amber-800">
              Mietverhältnisse sind beendet — Abrechnung fehlt vor Endabschluss
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {pendingDeposits.map((p) => (
              <Link
                key={p.tenancy_id}
                to={`/mietverhaeltnisse/${p.tenancy_id}?tab=kaution`}
                className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded px-3 py-2 hover:border-amber-300 hover:shadow-sm transition"
                data-testid={`pending-deposit-${p.tenancy_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-slate-700">{p.contract_number || p.tenancy_id.slice(0, 8)}</div>
                  <div className="text-sm text-slate-900 truncate">{(p.tenant_names || []).join(", ") || "—"}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Ende: {formatDate(p.end_date)} · Kaution: {formatEUR(p.deposit)}
                  </div>
                </div>
                <ArrowRight size={14} className="text-amber-700 shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity + alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold tracking-tight text-slate-900">
              {isAdmin ? "Letzte Aktivität" : "Mein Arbeitsbereich"}
            </h2>
            <span className="text-xs text-slate-500">
              {isAdmin
                ? `Audit-Log · ${(kpis.recent_activity || []).length} Einträge`
                : `${myTasks.length} offene Aufgaben · ${myTickets.length} Tickets`}
            </span>
          </div>
          {isAdmin ? (
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
              {(kpis.recent_activity || []).length === 0 && (
                <div className="px-5 py-6 text-sm text-slate-500">Noch keine Aktivität.</div>
              )}
              {(kpis.recent_activity || []).map((a, i) => (
                <div key={i} className="px-5 py-2.5 flex items-center justify-between text-sm" data-testid={`audit-row-${i}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0"></span>
                    <span className="font-medium text-slate-800 truncate">{prettyAction(a.action)}</span>
                    <span className="text-slate-500 text-xs">{a.entity_type}</span>
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums whitespace-nowrap ml-3">{formatDate(a.created_at)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto" data-testid="my-workspace">
              {myTasks.length === 0 && myTickets.length === 0 && (
                <div className="px-5 py-8 text-center text-sm text-slate-500">
                  <ListChecks size={28} className="mx-auto mb-2 text-slate-300" />
                  Aktuell keine offenen Aufgaben oder Tickets für dich.
                </div>
              )}
              {myTasks.length > 0 && (
                <div className="px-5 py-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Meine offenen Aufgaben</div>
                  <ul className="space-y-1.5">
                    {myTasks.map((t) => (
                      <li key={t.id} className="flex items-center justify-between text-sm" data-testid={`my-task-${t.id}`}>
                        <Link to="/aufgaben" className="flex items-center gap-2 min-w-0 hover:text-slate-900">
                          <ListChecks size={14} className={t.priority === "high" ? "text-rose-600" : "text-slate-400"} />
                          <span className="truncate text-slate-800">{t.title}</span>
                        </Link>
                        <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap ml-3">{t.due_date ? formatDate(t.due_date) : "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {myTickets.length > 0 && (
                <div className="px-5 py-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Mir zugewiesene Tickets</div>
                  <ul className="space-y-1.5">
                    {myTickets.map((t) => (
                      <li key={t.id} className="flex items-center justify-between text-sm" data-testid={`my-ticket-${t.id}`}>
                        <Link to={`/tickets/${t.id}`} className="flex items-center gap-2 min-w-0 hover:text-slate-900">
                          <Wrench size={14} className="text-slate-400" />
                          <span className="truncate text-slate-800">{t.title}</span>
                        </Link>
                        <span className="text-xs text-slate-500 whitespace-nowrap ml-3 uppercase tracking-wider">{t.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3 border-b border-slate-200">
            <h2 className="text-sm font-semibold tracking-tight text-slate-900">Hinweise</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {kpis.overdue_tasks > 0 && (
              <Alert icon={<Warning size={16} weight="fill" />} color="rose"
                title={`${kpis.overdue_tasks} überfällige Aufgabe(n)`}
                to="/aufgaben" />
            )}
            {kpis.open_tickets > 0 && (
              <Alert icon={<Wrench size={16} weight="fill" />} color="amber"
                title={`${kpis.open_tickets} offene Tickets`}
                to="/tickets" />
            )}
            {kpis.unmatched_bank > 0 && (
              <Alert icon={<Bank size={16} weight="fill" />} color="amber"
                title={`${kpis.unmatched_bank} Bankbuchung(en) klären`}
                to="/bank" />
            )}
            {kpis.arrears_amount > 0 && (
              <Alert icon={<CurrencyEur size={16} weight="fill" />} color="rose"
                title={`Mietrückstände ${formatEUR(kpis.arrears_amount)}`}
                to="/mietverhaeltnisse" />
            )}
            {kpis.vacant_units > 0 && (
              <Alert icon={<House size={16} weight="fill" />} color="slate"
                title={`${kpis.vacant_units} Einheit(en) leerstehend`}
                to="/objekte" />
            )}
            {kpis.ending_soon > 0 && (
              <Alert icon={<Warning size={16} />} color="amber"
                title={`${kpis.ending_soon} Vertrag/Verträge enden in 30 Tagen`}
                to="/mietverhaeltnisse" />
            )}
            {kpis.rent_adjustments_due > 0 && (
              <Alert icon={<CurrencyEur size={16} weight="fill" />} color="emerald"
                title={`Mietanpassung möglich: ${kpis.rent_adjustments_due} Vertrag/Verträge (Staffel / Index)`}
                to="/mietverhaeltnisse" />
            )}
            {kpis.overdue_tasks === 0 && kpis.arrears_amount === 0 && kpis.vacant_units === 0 && kpis.ending_soon === 0 && kpis.open_tickets === 0 && kpis.unmatched_bank === 0 && !kpis.rent_adjustments_due && (
              <div className="px-5 py-6 text-sm text-emerald-700 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                Alles im grünen Bereich.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2026-08 — Meine offenen Entwürfe (letzte 7 Tage). Ein-Klick-Weiterbearbeitung. */}
      {myDrafts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md" data-testid="dashboard-drafts-widget">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <NotePencil size={14} /> Meine offenen Entwürfe
            </h2>
            <span className="text-xs text-slate-500">
              {myDrafts.length} Entwurf/Entwürfe (letzte 7 Tage)
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {myDrafts.map((d) => {
              const age = ageInDays(d.updated_at);
              const chn = (d.channels || []).join("+") || "—";
              return (
                <button
                  key={d.id}
                  onClick={() => setActiveDraft(d)}
                  className="w-full px-5 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`dashboard-draft-${d.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {d.subject || <span className="italic text-slate-400">(ohne Betreff)</span>}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 rounded">
                        {chn}
                      </span>
                      {d.email_to && (
                        <span className="truncate">
                          <EnvelopeSimple size={11} className="inline mr-0.5" /> {d.email_to}
                        </span>
                      )}
                      <span className="text-slate-400 ml-auto whitespace-nowrap">
                        {age === 0 ? "heute" : age === 1 ? "gestern" : `vor ${age} Tagen`} · {formatDate(d.updated_at)}
                      </span>
                    </div>
                  </div>
                  <PaperPlaneTilt size={14} className="text-slate-400 shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* MessagingDialog zum Weiterbearbeiten eines Drafts (aus dem Widget geöffnet) */}
      {activeDraft && (
        <MessagingDialog
          open
          onOpenChange={(v) => {
            if (!v) {
              setActiveDraft(null);
              // Draft-Liste nach Schließen erneut laden
              api.get("/messages/drafts", { params: { limit: 30 } })
                .then((r) => {
                  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
                  setMyDrafts((r.data || []).filter((d) => new Date(d.updated_at).getTime() >= cutoff).slice(0, 10));
                })
                .catch(() => {});
            }
          }}
          context={{
            draft_id: activeDraft.id,
            contact_id: activeDraft.contact_id || undefined,
            tenancy_id: activeDraft.tenancy_id || undefined,
            property_id: activeDraft.property_id || undefined,
            vorgang_id: activeDraft.vorgang_id || undefined,
          }}
          defaultSubject={activeDraft.subject || ""}
          defaultEmail={activeDraft.email_to || ""}
        />
      )}
    </div>
  );
}

function ageInDays(iso) {
  const d = new Date(iso);
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}

function InlineStat({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-slate-300">
      <span className="text-slate-500">{icon}</span>
      <span className="font-semibold text-white tabular-nums">{value}</span>
      <span className="text-slate-400">{label}</span>
    </div>
  );
}

function Kpi({ to, icon, label, value, sub, subColor, testid, accent }) {
  const accentClass = accent === "amber"
    ? "before:bg-amber-500"
    : accent === "rose"
    ? "before:bg-rose-500"
    : "before:bg-slate-900";
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative block bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400 hover:shadow-sm transition-all duration-150 overflow-hidden before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:opacity-0 hover:before:opacity-100 before:transition-opacity ${accentClass}`}
    >
      <div className="flex items-center justify-between text-slate-500 mb-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold">{label}</div>
        {icon}
      </div>
      <div className="text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">{value}</div>
      <div className={`text-xs mt-1 ${subColor}`}>{sub}</div>
    </Link>
  );
}

function Alert({ icon, color, title, to }) {
  const colors = {
    rose: "text-rose-700",
    amber: "text-amber-700",
    slate: "text-slate-700",
    emerald: "text-emerald-700",
  };
  return (
    <Link to={to} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors group">
      <div className={colors[color]}>{icon}</div>
      <div className="text-sm text-slate-800 flex-1">{title}</div>
      <ArrowRight size={14} className="text-slate-400 group-hover:text-slate-700" />
    </Link>
  );
}

function DbExportButton() {
  const [busy, setBusy] = useState(false);
  const [mismatchCount, setMismatchCount] = useState(null);
  const [mismatchOpen, setMismatchOpen] = useState(false);
  const [mismatchItems, setMismatchItems] = useState([]);
  useEffect(() => {
    api.get("/admin/name-synonym-mismatches", { params: { limit: 100 } })
      .then((r) => { setMismatchCount(r.data?.count || 0); setMismatchItems(r.data?.items || []); })
      .catch(() => setMismatchCount(0));
  }, []);
  const run = async () => {
    if (busy) return;
    if (!window.confirm("DSGVO-Export erzeugen? Der Download kann je nach Datenmenge einige Sekunden dauern.")) return;
    setBusy(true);
    try {
      const res = await api.get("/admin/db-export", { responseType: "blob", timeout: 180000 });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m?.[1] || `domivus-export-${Date.now()}.tar.gz`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a);
      a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export heruntergeladen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 flex items-center gap-2 text-xs text-slate-300 flex-wrap">
      <button
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded border border-white/20 bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition disabled:opacity-50"
        title="DSGVO-konformer mongodump-Download"
        data-testid="db-export-btn"
      >
        <DownloadSimple size={14} />
        {busy ? "Erstelle Export…" : "DSGVO-Datenbank-Export"}
      </button>
      <span className="text-[10px] text-slate-400">Admin-Funktion · mongodump als tar.gz</span>
      {mismatchCount !== null && mismatchCount > 0 && (
        <button
          onClick={() => setMismatchOpen(true)}
          className="inline-flex items-center gap-1.5 rounded border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 px-3 py-1.5 text-xs font-medium text-amber-100 transition"
          data-testid="synonym-mismatch-btn"
          title="Bank-Buchungen ohne Umlaut-Match"
        >
          <Warning size={14} />
          {mismatchCount} Umlaut-Nachpflege
        </button>
      )}
      {mismatchOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setMismatchOpen(false)}>
          <div className="bg-white rounded-md max-w-4xl w-full max-h-[80vh] overflow-y-auto text-slate-900" onClick={(e) => e.stopPropagation()} data-testid="synonym-mismatch-dialog">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <div className="text-sm font-semibold">Umlaut-Nachpflege · Bank-Auftraggeber ohne Kontakt-Match</div>
                <div className="text-[11px] text-slate-500">Bereits zugeordnete Buchungen, deren Auftraggeber-Name in keiner Umlaut-Variante zu den Kontakt-Synonymen passt — bitte Kontakt-Schreibweise ergänzen.</div>
              </div>
              <button onClick={() => setMismatchOpen(false)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left border-b">
                <tr>
                  <th className="px-3 py-2">Datum</th>
                  <th className="px-3 py-2">Auftraggeber (Bank)</th>
                  <th className="px-3 py-2">Vertragsnr.</th>
                  <th className="px-3 py-2">Mieter</th>
                  <th className="px-3 py-2">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {mismatchItems.map((r) => (
                  <tr key={r.bank_tx_id} className="border-b border-slate-100" data-testid={`mismatch-row-${r.bank_tx_id}`}>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{formatDate(r.booking_date)}</td>
                    <td className="px-3 py-2 font-medium">{r.counterparty_name}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">{r.contract_number || "—"}</td>
                    <td className="px-3 py-2">{(r.tenant_names || []).join(", ")}</td>
                    <td className="px-3 py-2">
                      <Link to={`/bank?tx=${r.bank_tx_id}`} className="text-teal-700 hover:text-teal-900 underline">Bank öffnen</Link>
                    </td>
                  </tr>
                ))}
                {mismatchItems.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-slate-500">Keine Nachpflege nötig.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function prettyAction(a) {
  const map = {
    login: "Anmeldung", logout: "Abmeldung",
    user_created: "Benutzer angelegt", user_updated: "Benutzer aktualisiert",
    user_deactivated: "Benutzer deaktiviert", password_changed: "Passwort geändert",
    password_reset: "Passwort zurückgesetzt",
    contact_created: "Kontakt angelegt", contact_updated: "Kontakt aktualisiert", contact_deleted: "Kontakt gelöscht",
    property_created: "Objekt angelegt", property_updated: "Objekt aktualisiert", property_deleted: "Objekt gelöscht",
    building_created: "Gebäude angelegt", unit_created: "Einheit angelegt", unit_deleted: "Einheit gelöscht",
    tenancy_created: "Mietverhältnis angelegt", tenancy_updated: "Mietverhältnis aktualisiert", tenancy_archived: "Mietverhältnis archiviert",
    posting_added: "Buchung erfasst", soll_generated: "Sollstellung erzeugt",
    task_created: "Aufgabe angelegt", task_updated: "Aufgabe aktualisiert", task_deleted: "Aufgabe gelöscht",
    document_uploaded: "Dokument hochgeladen", document_deleted: "Dokument gelöscht",
    ticket_created: "Ticket angelegt", ticket_updated: "Ticket aktualisiert",
    ticket_comment_added: "Kommentar hinzugefügt", ticket_deleted: "Ticket gelöscht",
    bank_import: "Bankimport ausgeführt", bank_manual_assign: "Bank-Buchung manuell zugeordnet",
  };
  return map[a] || a;
}
